<template>
  <section
    id="ability"
    ref="abilityRef"
    class="ability transition-all duration-1000 ease-out landing-container py-120px lt-sm:py-120px lt-sm:px-32px"
    :class="[isAbilityVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-50px']"
  >
    <h2
      class="ability-title pf-sc-semibold font-s-36px text-#000000 flex-center lh-60px lt-sm:font-s-48px lt-sm:lh-60px"
    >
      一体化智慧解决方案
    </h2>
    <p
      class="ability-desc pf-sc-regular font-s-16px text-main/70 lh-30px flex-center mt-4px lt-sm:font-s-24px lt-sm:lh-40px lt-sm:text-center lt-sm:px-60px"
    >
      绘家科技提供从软件到硬件的完整解决方案，覆盖物业管理的全场景需求
    </p>
    <div class="ability-content mt-24px flex-center flex-col lt-sm:mt-40px">
      <div
        class="ability-tabs relative flex items-center rounded-14px border border-#ECEFF6 bg-#F6F8FD p-6px lt-sm:rounded-16px lt-sm:p-8px lt-sm:w-[calc(100%-40px)]"
        role="tablist"
        aria-label="解决方案分类"
      >
        <div
          class="ability-tab-indicator absolute w-136px h-[calc(100%-12px)] transition-all duration-300 ease-out rounded-8px bg-primary lt-sm:w-[calc((100%-16px)/3)] lt-sm:h-[calc(100%-16px)] lt-sm:rounded-12px"
          :style="abilityTabIndicatorStyle"
          aria-hidden="true"
        ></div>

        <div
          v-for="tab in abilityTabs"
          :id="`tab-${tab.id}`"
          :key="tab.title"
          class="ability-tab-item relative z-1 cursor-pointer py-14px px-20px text-center font-s-16px transition-colors duration-300 pf-sc-regular lt-sm:flex-1 lt-sm:px-0 lt-sm:py-20px lt-sm:font-s-24px focus-visible:outline-primary"
          :class="[activeAbilityTab.id === tab.id ? 'text-white' : 'text-main hover:text-primary']"
          role="tab"
          :aria-selected="activeAbilityTab.id === tab.id"
          :aria-controls="`panel-${tab.id}`"
          tabindex="0"
          @click="handleAbilityTabClick(tab)"
          @keydown.enter="handleAbilityTabClick(tab)"
          @keydown.space.prevent="handleAbilityTabClick(tab)"
        >
          {{ tab.title }}
        </div>
      </div>
      <div class="tab-content pt-45px lt-sm:pt-40px lt-sm:w-full">
        <Transition name="tab-fade" mode="out-in">
          <div
            :id="`panel-${activeAbilityTab.id}`"
            :key="activeAbilityTab.id"
            role="tabpanel"
            :aria-labelledby="`tab-${activeAbilityTab.id}`"
            class="ability-grid grid grid-cols-3 gap-24px lt-sm:grid-cols-2 lt-sm:gap-25px"
          >
            <section-ability-card
              v-for="ability in abilities[activeAbilityTab.id]"
              :key="ability.title"
              :ability="ability"
            />
          </div>
        </Transition>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
  import { abilityTabs, abilities } from '@/constants/common'

  const abilityRef = ref<HTMLElement | null>(null)
  const { isVisible: isAbilityVisible } = useScrollReveal(abilityRef)

  const activeAbilityTab = ref<AbilityTab>(abilityTabs[0] as AbilityTab)

  const abilityTabIndicatorStyle = computed(() => {
    const activeTabIndex = abilityTabs.findIndex((tab) => tab.id === activeAbilityTab.value.id)
    return {
      transform: `translateX(${activeTabIndex * 100}%)`,
    }
  })

  const handleAbilityTabClick = (tab: AbilityTab) => {
    activeAbilityTab.value = tab
  }
</script>

<style scoped>
  .ability {
    background-size: 100% 100%;
    background-image: url('@/assets/images/bg-ability.png');
  }

  @screen lt-sm {
    .ability {
      background-size: 100% 100%;
      background-image: url('@/assets/images/bg-ability-mobile.png');
    }
  }

  .tab-fade-enter-active {
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  .tab-fade-leave-active {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .tab-fade-enter-from {
    opacity: 0;
    transform: translateX(30px);
  }

  .tab-fade-leave-to {
    opacity: 0;
    transform: translateX(-30px);
  }

  .ability-grid > div {
    animation: card-slide-up 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
  }

  .ability-grid > div:nth-child(1) {
    animation-delay: 0.05s;
  }
  .ability-grid > div:nth-child(2) {
    animation-delay: 0.1s;
  }
  .ability-grid > div:nth-child(3) {
    animation-delay: 0.15s;
  }
  .ability-grid > div:nth-child(4) {
    animation-delay: 0.2s;
  }
  .ability-grid > div:nth-child(5) {
    animation-delay: 0.25s;
  }
  .ability-grid > div:nth-child(6) {
    animation-delay: 0.3s;
  }
  .ability-grid > div:nth-child(7) {
    animation-delay: 0.35s;
  }
  .ability-grid > div:nth-child(8) {
    animation-delay: 0.4s;
  }

  @keyframes card-slide-up {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
</style>
